import { computed, inject, type Signal } from '@angular/core';
import { injectProtectedEntry } from '@sbe-spinoff-client/app-shell';
import type { Routes } from '@angular/router';
import { SpinoffAuthService } from './spinoff-auth.service';
import { SpinoffBootGateService } from './spinoff-boot-gate.service';

/**
 * `AppShellRootComponent`의 `showBootLoading` 입력에 그대로 바인딩하는 signal을 반환한다.
 * 앱 최초 부팅 시 `SpinoffBootGateService.ensureStarted()`를 시작시키고, guard로 보호된
 * 경로(`injectProtectedEntry`)로 콜드 진입/새로고침한 경우에만 `spinoffAuthGuard`가
 * `auth.init()`(refresh API)을 기다리는 동안 로딩 상태를 켠다. 공개 경로(랜딩·로그인 등)는
 * 각 화면이 자체 로딩 상태를 표시하므로 여기서는 막지 않는다.
 *
 * `isAuthenticated()`가 먼저 true가 되면(예: 세션 교환 흐름에서 accessToken이 동기적으로
 * 먼저 세팅되는 경우) 백그라운드 `init()` 완료를 기다리지 않고 즉시 로딩을 끈다.
 *
 * `sbe-sms/user-web`의 원본 구현(2026-08-11)을 lib으로 승격한다. `sbe-commerce/user-web`이
 * 별도 판별 함수를 손으로 복제해 둔 것은 아직 이 훅으로 교체되지 않았다 — 그 이관은
 * 별도 후속 PR 범위다(이 훅 신설 자체가 그 이관을 의미하지 않는다, 코드 리뷰 지적).
 *
 * @param routes 앱의 최상위 `Routes` — `canActivate` 유무로 공개 경로를 유도한다
 *   (`injectProtectedEntry` 참고).
 */
export function injectSpinoffBootLoading(routes: Routes): Signal<boolean> {
  const auth = inject(SpinoffAuthService);
  const bootGate = inject(SpinoffBootGateService);
  const isProtectedEntry = injectProtectedEntry(routes);

  bootGate.ensureStarted();

  return computed(() => isProtectedEntry && !bootGate.completed() && !auth.isAuthenticated());
}
